import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ipGet, ipPost, ipError } from "../api";
import { PlannerError, PlannerPermissionsStatus, usePlannerPermissions } from "../components/PlannerState";
import { Card, PageHeader, KpiRow, Badge } from "../../components/dc";

// Phase 3: score the latest forecast against uploaded actuals, show headline
// accuracy + worst cells, and manage suggested usage corrections. Accepting a
// suggestion stages it into the settings draft (publish on the Settings tab).
export default function Accuracy() {
  const [data, setData] = useState(null);
  const [sugg, setSugg] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const permissions = usePlannerPermissions();

  const load = useCallback(() => {
    setError(""); setLoading(true);
    Promise.all([ipGet("/accuracy/latest"), ipGet("/suggestions?status=pending")]).then(([d, s]) => { setData(d?.scorecard ? d : null); setSugg(s.suggestions || []); }).catch(e => setError(ipError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setBusy(true);
    try { await ipPost("/accuracy/compute", { dc_scope: "ALL" }); toast.success("Scored latest run"); load(); }
    catch (e) { setError(ipError(e)); }
    finally { setBusy(false); }
  };

  const suggest = async () => {
    setBusy(true);
    try { const r = await ipPost("/accuracy/suggest"); toast.success(`${r.created} suggestion(s)`); load(); }
    catch (e) { setError(ipError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (id, action) => {
    setBusy(true);
    try { await ipPost(`/suggestions/${id}/${action}`); toast.success(action === "accept" ? "Staged into settings draft" : "Dismissed"); load(); }
    catch (e) { setError(ipError(e)); }
    finally { setBusy(false); }
  };

  const h = data?.scorecard?.headline || {};
  const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "—");
  const kpis = [
    { label: "Forecast error (WAPE)", value: pct(h.wape) },
    { label: "Bias (+ = over)", value: pct(h.bias_pct) },
    { label: "Hit rate", value: pct(h.hit_rate_pct) },
    { label: "Unforecast cells", value: String(h.unforecast?.lines ?? "—") },
  ];
  const worst = (data?.lines || []).filter((l) => l.actual || l.forecast).slice(0, 40);

  return (
    <div data-testid="ip-accuracy-page" style={{ minWidth: 0 }}>
      <PageHeader title="Accuracy" subtitle="How the last forecast held up against actuals, and where usage looks like it has drifted." />
      <PlannerError message={error} testId="ip-accuracy-error" retry={load} />
      <PlannerPermissionsStatus permissions={permissions} />
      {loading && <p data-testid="ip-accuracy-loading" role="status">Loading accuracy results…</p>}

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="ip-score-run" className="btn btn-primary" disabled={busy || !permissions.can_edit} onClick={compute}>{busy ? "Working…" : "Score latest run"}</button>
          <button data-testid="ip-find-corrections" className="btn btn-secondary" disabled={busy || !permissions.can_edit} onClick={suggest}>Find usage corrections</button>
          <span style={{ flex: 1 }} />
          {data?.scorecard && <span data-testid="ip-score-summary" style={{ fontSize: 13, color: "var(--color-text-3)" }}>Scored {data.scorecard.label} · observed actuals weeks only</span>}
        </div>
        {!loading && !error && !data && <p data-testid="ip-accuracy-empty" style={{ color: "var(--color-text-3)", marginTop: 10, marginBottom: 0 }}>Upload a part-actuals file on Inputs, then score the run.</p>}
      </Card>

      {data && <div data-testid="ip-accuracy-kpis"><KpiRow items={kpis} cols={4} /></div>}

      {sugg.length > 0 && (
        <Card style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Suggested usage corrections ({sugg.length})</div>
          <div style={{ overflowX: "auto" }}><table data-testid="ip-suggestions-table" className="data-table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>Part</th><th>Line</th><th>Drift</th><th style={{ textAlign: "right" }}>Scale</th><th>Why</th><th /></tr></thead>
            <tbody>
              {sugg.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{s.part}</td>
                  <td>{s.product_line}</td>
                  <td><Badge tone={s.direction === "high" ? "warn" : "muted"}>{s.direction}</Badge></td>
                  <td data-testid={`ip-suggestion-factor-${s.id}`} style={{ textAlign: "right" }}>×{s.base_scale ?? 1} → ×{s.target_scale ?? s.suggested_scale}</td>
                  <td style={{ color: "var(--color-text-3)" }}>{s.reason}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button data-testid={`ip-suggestion-accept-${s.id}`} disabled={busy || !permissions.can_edit} className="btn btn-primary btn-sm" onClick={() => decide(s.id, "accept")}>Accept</button>{" "}
                    <button data-testid={`ip-suggestion-dismiss-${s.id}`} disabled={busy || !permissions.can_edit} className="btn btn-secondary btn-sm" onClick={() => decide(s.id, "dismiss")}>Dismiss</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div style={{ fontSize: 12, color: "var(--color-text-4)", marginTop: 6 }}>Accepting stages a usage adjustment into the Settings draft; publish it on the Settings tab to apply.</div>
        </Card>
      )}

      {worst.length > 0 && (
        <Card style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Largest variances</div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="ip-accuracy-variances" className="data-table" style={{ width: "100%", fontSize: 13 }}>
              <thead><tr><th>Part</th><th>DC</th><th>Week</th><th style={{ textAlign: "right" }}>Forecast</th><th style={{ textAlign: "right" }}>Actual</th><th style={{ textAlign: "right" }}>Variance</th></tr></thead>
              <tbody>
                {worst.map((l, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "monospace" }}>{l.part}</td>
                    <td>{l.dc}</td><td>{l.week}</td>
                    <td style={{ textAlign: "right" }}>{l.forecast}</td>
                    <td style={{ textAlign: "right" }}>{l.actual}</td>
                    <td style={{ textAlign: "right", color: l.variance > 0 ? "#fbbf24" : "#60a5fa" }}>{l.variance > 0 ? "+" : ""}{l.variance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
